import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import Stack from '@mui/material/Stack';
import { useAtom } from 'jotai';
import { Controller, useFormContext } from 'react-hook-form';

import JsonInput from '@/components/JsonInput';
import LoadingIndicator from '@/components/LoadingIndicator';
import ErrorMessage from '@/features/SourceCreate/ErrorMessage/ErrorMessage';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import { useDeviceDialog, useDeviceForm } from '../DeviceCreate.hooks';
import {
  deviceNameAtom,
  deviceSizeAtom,
  deviceTypesAtom,
  hostNamePrefixAtom,
  selectedDeviceSizeIdAtom,
  selectedDeviceTypeIdAtom
} from '../DeviceCreate.store';
import useStyles from './DeviceCreateForm.styles';

const DeviceCreateForm = () => {
  const { register, control, formState } = useFormContext();
  const { errors } = formState;

  const { mode } = useDeviceDialog();
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const [deviceTypesData] = useAtom(deviceTypesAtom);
  const [deviceSizeData] = useAtom(deviceSizeAtom);
  const [hostNamePrefix] = useAtom(hostNamePrefixAtom);
  const [deviceName] = useAtom(deviceNameAtom);
  const [selectedDeviceTypeId] = useAtom(selectedDeviceTypeIdAtom);
  const [selectedDeviceSizeId] = useAtom(selectedDeviceSizeIdAtom);
  const { classes } = useStyles();

  const {
    handleDeviceTypeChange,
    handleDeviceSizeChange,
    handleDeviceNameChange,
    deviceDetailQuery
  } = useDeviceForm();

  return (
    <Box sx={{ flexGrow: 1 }}>
      <LoadingIndicator isLoading={deviceDetailQuery?.isRefetching} />
      <Grid container spacing={2}>
        {/* Organization name from the global organization dropdown */}
        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
          <Grid container>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <InputLabel htmlFor="device-name-input">Organization</InputLabel>
              <FormControl fullWidth>
                <TextField
                  name="organization_name"
                  value={selectedOrgData?.name}
                  disabled
                />
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        {/* Device Type */}
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <InputLabel htmlFor="device-name-input">Device Type *</InputLabel>
          <FormControl fullWidth>
            <Controller
              name="device_type_id"
              control={control}
              rules={{
                min: { value: 1, message: 'Device Type is required' }
              }}
              defaultValue={selectedDeviceTypeId}
              render={({ field }) => (
                <Select
                  sx={{ width: '100%' }}
                  {...field}
                  error={!!formState?.errors?.device_type_id}
                  onChange={(event) =>
                    handleDeviceTypeChange(event.target.value, field.onChange)
                  }
                  value={field.value}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected?.length === 0 || selected === 0) {
                      return <em>Select Device Type</em>;
                    }
                    return (
                      deviceTypesData?.find((item) => item.id === selected)
                        ?.type || ''
                    );
                  }}
                  disabled={mode === 'Edit'}
                >
                  <MenuItem value={0}>
                    <em>Select Device Type</em>
                  </MenuItem>
                  {/* skip lima device type */}
                  {deviceTypesData?.map(
                    (item) =>
                      item.id !== 1 && (
                        <MenuItem key={item.id} value={item.id}>
                          {item.type}
                        </MenuItem>
                      )
                  )}
                </Select>
              )}
            />
            {errors?.device_type_id && (
              <ErrorMessage message={errors?.device_type_id?.message} />
            )}
          </FormControl>
        </Grid>

        {/* Device name from the devices table */}
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <InputLabel htmlFor="device-name-input">
            Device Name <span> *</span>
          </InputLabel>
          <FormControl fullWidth>
            <Stack direction="row">
              <Stack sx={{ width: '60%' }} className={classes.fixedLabel}>
                {hostNamePrefix}
              </Stack>
              <TextField
                {...register('host_name', {
                  required: 'Device Number is required'
                })}
                type="number"
                value={deviceName}
                onChange={handleDeviceNameChange}
                error={!!formState.errors.host_name}
                slotProps={{
                  htmlInput: {
                    min: 1,
                    max: 99
                  }
                }}
                className={classes.input}
                disabled={mode === 'Edit'}
              />
            </Stack>
            {errors.host_name && (
              <ErrorMessage message={errors.host_name?.message} />
            )}
          </FormControl>
        </Grid>

        {/* Display name from the devices table */}
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <InputLabel htmlFor="display-name-input">Display Name</InputLabel>
          <FormControl fullWidth>
            <Stack direction="row">
              <TextField
                {...register('display_name')}
                type="text"
                className={classes.input}
              />
            </Stack>
          </FormControl>
        </Grid>

        {/* Device Size */}
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <InputLabel htmlFor="device-name-input">Device Size *</InputLabel>
          <FormControl fullWidth>
            <Controller
              name="device_size_id"
              control={control}
              defaultValue={selectedDeviceSizeId}
              rules={{
                min: { value: 1, message: 'Device Size is required' }
              }}
              render={({ field }) => (
                <Select
                  sx={{ width: '100%' }}
                  {...field}
                  error={!!formState?.errors?.device_size_id}
                  onChange={(event) =>
                    handleDeviceSizeChange(event.target.value, field.onChange)
                  }
                  value={field.value}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected?.length === 0 || selected === 0) {
                      return <em>Select Device Size</em>;
                    }
                    return (
                      deviceSizeData?.find((item) => item.id === selected)
                        ?.device_size || ''
                    );
                  }}
                >
                  <MenuItem value={0}>
                    <em>Select Device Size</em>
                  </MenuItem>
                  {deviceSizeData?.map(
                    (item) =>
                      item.device_type_id === selectedDeviceTypeId && (
                        <MenuItem key={item.id} value={item?.id}>
                          {item?.device_size}
                        </MenuItem>
                      )
                  )}
                </Select>
              )}
            />
            {errors?.device_size_id && (
              <ErrorMessage message={errors?.device_size_id?.message} />
            )}
          </FormControl>
        </Grid>

        {/* Configuration */}
        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
          <InputLabel htmlFor="device-name-input">
            Configuration (JSON; LF proxy example:{' '}
            {'{"enable_graylog_proxy": true}'})
          </InputLabel>
          <FormControl fullWidth className={classes.DescriptionFormStyle}>
            <JsonInput
              {...register(`configuration`)}
              rows={7}
              name="configuration"
            />
          </FormControl>
        </Grid>

        {/* Description */}
        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
          <InputLabel htmlFor="device-name-input">Description</InputLabel>
          <FormControl fullWidth>
            <TextField
              {...register('description', {})}
              name="description"
              multiline
              maxRows={10}
            />
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DeviceCreateForm;
